import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { seguradoraService } from '@/services/api';
import { Seguradora, PaginationParams } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const SeguradorasPage: React.FC = () => {
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateSeguradoras } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const { data: seguradorasData, isLoading, error } = useQuery({
    queryKey: ['seguradoras', filters],
    queryFn: () => seguradoraService.list(filters),
  });

  const deleteSeguradoraMutation = useMutation({
    mutationFn: seguradoraService.delete,
    onSuccess: () => {
      invalidateSeguradoras();
      handleSuccess('Seguradora excluída com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir seguradora');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Seguradora',
      'Tem certeza que deseja excluir esta seguradora? Esta ação não pode ser desfeita.',
      async () => {
        await deleteSeguradoraMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar seguradoras</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seguradoras</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie as seguradoras cadastradas no sistema
          </p>
        </div>
        <Link to="/seguradoras/nova">
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            Nova Seguradora
          </Button>
        </Link>
      </div>

      {/* Seguradoras List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Seguradoras</CardTitle>
              <CardDescription>
                {seguradorasData?.pagination.total || 0} seguradoras encontradas
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : seguradorasData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma seguradora encontrada</p>
              <Link to="/seguradoras/nova">
                <Button className="mt-4">Cadastrar primeira seguradora</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {seguradorasData?.data.map((seguradora: Seguradora) => (
                <div
                  key={seguradora.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 p-2 bg-orange-100 rounded-md">
                        <ShieldCheckIcon className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{seguradora.nome}</h3>
                        
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Link to={`/seguradoras/${seguradora.id}/editar`}>
                        <Button variant="outline" size="sm">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDelete(seguradora.id)}
                        disabled={deleteSeguradoraMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {seguradorasData && seguradorasData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!seguradorasData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {seguradorasData.pagination.page} de {seguradorasData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!seguradorasData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteSeguradoraMutation.isPending}
      />
    </div>
  );
};
